"use client";

import { CalendarDays, Loader2 } from "lucide-react";
import { FormEvent, useState } from "react";
import type { HomepagePayload } from "@/lib/homepage";
import { sectionFor } from "./types";

type Props = {
  data: HomepagePayload;
};

const inputClass =
  "w-full px-4 py-3 rounded-xl border border-gray-200 focus:border-[var(--color-primary)] focus:ring-2 focus:ring-[#b5e7e1] outline-none transition-all";
const labelClass = "block text-sm font-semibold text-gray-700 mb-2";

function localDateValue(date = new Date()) {
  const offset = date.getTimezoneOffset();
  return new Date(date.getTime() - offset * 60_000).toISOString().slice(0, 10);
}

export default function AppointmentCalendarSection({ data }: Props) {
  const section = sectionFor(data, "appointment_calendar");
  const [status, setStatus] = useState<"idle" | "saving" | "success" | "error">("idle");
  const [errorMessage, setErrorMessage] = useState("");

  async function submit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    const form = event.currentTarget;
    setStatus("saving");
    setErrorMessage("");
    const formData = new FormData(form);

    try {
      const response = await fetch("/api/appointments", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(Object.fromEntries(formData.entries())),
      });
      if (!response.ok) {
        const result = await response.json().catch(() => null);
        setErrorMessage(typeof result?.error === "string" ? result.error : "Please check your details and try again.");
        setStatus("error");
        return;
      }

      form.reset();
      setStatus("success");
    } catch (error) {
      console.error("Appointment submit failed:", error);
      setErrorMessage("Something went wrong. Please try again.");
      setStatus("error");
    }
  }

  return (
    <section id="book-appointment" className="bg-white px-6 py-16 lg:px-10">
      <div className="mx-auto grid max-w-7xl gap-12 md:grid-cols-[1.15fr_0.85fr]">
      <div className="rounded-[2rem] border border-gray-100 bg-white p-8 shadow-xl">
        <h2 className="mb-6 text-3xl font-extrabold text-[#2565ad] md:text-4xl">{section.title || "Book an appointment:"}</h2>
        <form onSubmit={submit} className="space-y-5">
          <fieldset disabled={status === "saving"} className="space-y-5 disabled:opacity-70">
            <div className="grid gap-5 sm:grid-cols-2">
              <div>
                <label htmlFor="booking-first-name" className={labelClass}>First Name *</label>
                <input id="booking-first-name" name="firstName" required placeholder="Jane" className={inputClass} />
              </div>
              <div>
                <label htmlFor="booking-phone" className={labelClass}>Phone *</label>
                <input id="booking-phone" name="phone" type="tel" required placeholder="07123 456789" className={inputClass} />
              </div>
            </div>
            <div>
              <label htmlFor="booking-address" className={labelClass}>Address *</label>
              <input id="booking-address" name="address" required placeholder="Your address" className={inputClass} />
            </div>
            <div>
              <label htmlFor="booking-email" className={labelClass}>Email *</label>
              <input id="booking-email" name="email" type="email" required placeholder="jane@example.com" className={inputClass} />
            </div>
            <div className="grid gap-5 sm:grid-cols-2">
              <div>
                <label htmlFor="booking-date" className={labelClass}>Preferred date *</label>
                <input id="booking-date" name="preferredDate" type="date" min={localDateValue()} required className={inputClass} />
              </div>
              <div>
                <label htmlFor="booking-time" className={labelClass}>Preferred time *</label>
                <input id="booking-time" name="preferredTime" type="time" required className={inputClass} />
              </div>
            </div>
            <div>
              <label htmlFor="booking-comments" className={labelClass}>Comments *</label>
              <textarea id="booking-comments" name="comments" required rows={4} placeholder="Tell us about your childcare needs..." className={`${inputClass} resize-none`} />
            </div>
            <button
              type="submit"
              disabled={status === "saving"}
              className="flex w-full transform items-center justify-center gap-2 rounded-full bg-[var(--color-primary)] py-4 text-lg font-bold text-white shadow-lg transition-all duration-300 hover:-translate-y-1 hover:bg-[var(--color-primary-dark)] disabled:translate-y-0 disabled:opacity-70"
            >
              {status === "saving" && <Loader2 className="animate-spin" size={20} />}
              {status === "saving" ? "Submitting..." : "Submit"}
            </button>
          </fieldset>
          {status === "success" && <p role="status" className="font-bold text-green-700">Thank you. Your preferred date and time have been submitted.</p>}
          {status === "error" && <p role="alert" className="font-bold text-red-700">{errorMessage}</p>}
        </form>
      </div>

      <div className="flex flex-col items-center justify-center text-center">
        <h2 className="mb-12 text-5xl font-extrabold text-[#ed2f2f] md:text-6xl">{section.subtitle || "Parent Calendar:"}</h2>
        <p className="max-w-2xl text-3xl font-extrabold leading-snug text-[#ed2f2f]">
          {section.body || "This is one more way by which we keep our parents up to date with key dates, events, and relevant information."}
        </p>
        <a href="/parent-calendar" aria-label="Open parent calendar" className="mt-12 grid h-24 w-24 place-items-center rounded-full bg-[#10bdb7] text-white transition hover:scale-105">
          <CalendarDays size={54} />
        </a>
      </div>
      </div>
    </section>
  );
}
